"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RecursiveOperationSession = exports.RECURSIVE_OPERATION_TIMEOUT = void 0;
const eventemitter3_1 = __importDefault(require("eventemitter3"));
const uuid_1 = require("uuid");
const DhtRpc_1 = require("../../../generated/packages/dht/protos/DhtRpc");
const ListeningRpcCommunicator_1 = require("../../transport/ListeningRpcCommunicator");
const Contact_1 = require("../contact/Contact");
const SortedContactList_1 = require("../contact/SortedContactList");
const RecursiveOperationSessionRpcLocal_1 = require("./RecursiveOperationSessionRpcLocal");
const identifiers_1 = require("../../identifiers");
exports.RECURSIVE_OPERATION_TIMEOUT = 10 * 1000;
class RecursiveOperationSession extends eventemitter3_1.default {
    id = (0, uuid_1.v4)();
    rpcCommunicator;
    results;
    foundData = new Map();
    allKnownHops = new Set();
    reportedHops = new Set();
    timeoutTask;
    completionEventEmitted = false;
    noCloserNodesReceivedCounter = 0;
    noCloserNodesReceivedFrom = new Set();
    options;
    constructor(options) {
        super();
        this.options = options;
        this.results = new SortedContactList_1.SortedContactList({
            referenceId: options.targetId,
            maxSize: 10, // TODO use options option or named constant?
            allowToContainReferenceId: true
        });
        this.rpcCommunicator = new ListeningRpcCommunicator_1.ListeningRpcCommunicator(this.id, options.transport, {
            rpcRequestTimeout: exports.RECURSIVE_OPERATION_TIMEOUT
        });
        this.registerLocalRpcMethods();
    }
    registerLocalRpcMethods() {
        const rpcLocal = new RecursiveOperationSessionRpcLocal_1.RecursiveOperationSessionRpcLocal({
            onResponseReceived: (remoteNodeId, routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound) => {
                this.onResponseReceived(remoteNodeId, routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound);
            }
        });
        this.rpcCommunicator.registerRpcNotification(DhtRpc_1.RecursiveOperationResponse, 'sendResponse', (req, context) => rpcLocal.sendResponse(req, context));
    }
    start(serviceId) {
        const routeMessage = this.wrapRequest(serviceId);
        this.options.doRouteRequest(routeMessage);
    }
    wrapRequest(serviceId) {
        const request = {
            sessionId: this.getId(),
            operation: this.options.operation
        };
        const msg = {
            messageId: (0, uuid_1.v4)(),
            serviceId,
            body: {
                oneofKind: 'recursiveOperationRequest',
                recursiveOperationRequest: request
            }
        };
        const routeMessage = {
            message: msg,
            requestId: (0, uuid_1.v4)(),
            target: (0, identifiers_1.toDhtAddressRaw)(this.options.targetId),
            sourcePeer: this.options.localPeerDescriptor,
            reachableThrough: [],
            routingPath: [],
            parallelRootNodeIds: []
        };
        return routeMessage;
    }
    isCompleted() {
        const unreportedHops = new Set(this.allKnownHops);
        this.reportedHops.forEach((id) => {
            unreportedHops.delete(id);
        });
        if (this.noCloserNodesReceivedCounter >= 1 && unreportedHops.size === 0) {
            if (this.options.operation === DhtRpc_1.RecursiveOperation.FETCH_DATA
                && (this.hasNonStaleData() || this.noCloserNodesReceivedCounter >= this.options.waitedRoutingPathCompletions)) {
                return true;
            }
            else if (this.options.operation === DhtRpc_1.RecursiveOperation.FETCH_DATA) {
                return false;
            }
            return true;
        }
        return false;
    }
    hasNonStaleData() {
        return Array.from(this.foundData.values()).some((entry) => entry.stale === false);
    }
    onResponseReceived(remoteNodeId, routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound) {
        this.addKnownHops(routingPath);
        if (routingPath.length >= 1) {
            this.setHopAsReported(routingPath[routingPath.length - 1]);
        }
        closestConnectedNodes.forEach((descriptor) => {
            this.results.addContact(new Contact_1.Contact(descriptor));
        });
        this.processFoundData(dataEntries);
        if (noCloserNodesFound || this.noCloserNodesReceivedFrom.has(remoteNodeId)) {
            this.onNoCloserPeersFound(remoteNodeId);
        }
    }
    addKnownHops(routingPath) {
        const localNodeId = (0, identifiers_1.toNodeId)(this.options.localPeerDescriptor);
        routingPath.forEach((desc) => {
            const newNodeId = (0, identifiers_1.toNodeId)(desc);
            if (localNodeId !== newNodeId) {
                this.allKnownHops.add(newNodeId);
            }
        });
    }
    setHopAsReported(desc) {
        const localNodeId = (0, identifiers_1.toNodeId)(this.options.localPeerDescriptor);
        const newNodeId = (0, identifiers_1.toNodeId)(desc);
        if (localNodeId !== newNodeId) {
            this.reportedHops.add(newNodeId);
        }
        if (this.isCompleted()) {
            if (!this.completionEventEmitted && this.isCompleted()) {
                if (this.timeoutTask) {
                    clearTimeout(this.timeoutTask);
                    this.timeoutTask = undefined;
                }
                this.emit('completed');
                this.completionEventEmitted = true;
            }
        }
    }
    processFoundData(dataEntries) {
        dataEntries.forEach((entry) => {
            const creatorNodeId = (0, identifiers_1.toDhtAddress)(entry.creator);
            const existingEntry = this.foundData.get(creatorNodeId);
            if (!existingEntry || existingEntry.createdAt < entry.createdAt
                || (existingEntry.createdAt <= entry.createdAt && entry.deleted)) {
                this.foundData.set(creatorNodeId, entry);
            }
        });
    }
    onNoCloserPeersFound(remoteNodeId) {
        this.noCloserNodesReceivedCounter += 1;
        this.noCloserNodesReceivedFrom.add(remoteNodeId);
        if (this.isCompleted()) {
            this.emit('completed');
            this.completionEventEmitted = true;
            if (this.timeoutTask) {
                clearTimeout(this.timeoutTask);
                this.timeoutTask = undefined;
            }
        }
        else if (!this.timeoutTask && !this.completionEventEmitted) {
            this.timeoutTask = setTimeout(() => {
                if (!this.completionEventEmitted) {
                    this.emit('completed');
                    this.completionEventEmitted = true;
                }
            }, 4000); // TODO use options option or named constant?
        }
    }
    getResults() {
        return {
            closestNodes: this.results.getClosestContacts().map((contact) => contact.getPeerDescriptor()),
            dataEntries: Array.from(this.foundData.values())
        };
    }
    getId() {
        return this.id;
    }
    stop() {
        if (this.timeoutTask) {
            clearTimeout(this.timeoutTask);
            this.timeoutTask = undefined;
        }
        this.rpcCommunicator.destroy();
        this.emit('completed');
    }
}
exports.RecursiveOperationSession = RecursiveOperationSession;
//# sourceMappingURL=RecursiveOperationSession.js.map